import type { Contact, PreUser, User } from './user'

export interface Attachment {
  id: number
  file_url: string
  original_filename: string
  file_size: number
  content_type_str: string
  uploaded_by?: User
  created_date: string
}

export enum IntervalType {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  BIWEEKLY = 'biweekly',
  MONTHLY = 'monthly',
  REALTIME = 'realtime',
}

export enum PaymentType {
  RATE_TYPE = 'RATE_TYPE',
  MILESTONE = 'MILESTONE',
  PROGRESS = 'PROGRESS',
}

export interface PaymentScheduleEntry {
  deliverable: string
  due_date: string
  pay_amount: string | number
  fee_amount: string | number
  completed: boolean
  completed_date?: string
}

export enum ProjectStatus {
  DRAFT = 'DRAFT',
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  DECLINED = 'DECLINED',
}

export enum InvitationStatus {
  PENDING_INVITATIONS = 'PENDING_INVITATIONS',
  PARTIALLY_ACCEPTED = 'PARTIALLY_ACCEPTED',
  FULLY_ACCEPTED = 'FULLY_ACCEPTED',
}

export interface ProjectInvitation {
  id: number
  email: string
  accepted: boolean
  created_date: string
}

// Original sender snapshot for downstream projects
export interface OriginalSenderSnapshot {
  first_name: string
  last_name: string
  email: string
  company_name?: string
  stakeholder_type: string
}

// Party snapshot for parent project parties (immutable historical info)
export interface PartySnapshot {
  first_name: string
  last_name: string
  email: string
  phone?: string
  company_name?: string
  company_address?: string
}

// All parties from parent project (for sub-contract reference display)
export interface ParentProjectParties {
  contractors: PartySnapshot[]
  brokers: PartySnapshot[]
  site_supervisors: PartySnapshot[]
  sub_haulers: PartySnapshot[]
}

// Sender stakeholder type
export type SenderStakeholderType = 'CONTRACTOR' | 'BROKER' | 'SUB_HAULER'

export interface Project {
  id: number
  created_date: string
  updated_date: string
  status: ProjectStatus
  date: string
  created_by: User
  job_site: string
  job_id: string
  po_number: string
  label: string
  starting_date: string
  completion_date: string
  pickup_address: string
  dropoff_address: string
  third_party_location?: string
  material: string
  rate_type: string
  pay_rate: string
  trucks_number: string
  truck_types: string[]
  volume: string
  notes?: string
  invoice_interval: IntervalType
  status_interval: IntervalType
  assigned_brokers: number[]
  assigned_contractors: number[]
  site_supervisors: User[] // API response returns full user objects
  site_supervisor_ids?: number[] // Form data uses user IDs
  project_managers?: User[]
  administrators?: User[]
  broker_contacts: number[]
  contractor_contacts: number[]
  brokers: User[]
  contractors: User[]
  site_supervisor_users: User[]
  broker_preusers: PreUser[]
  contractor_preusers: PreUser[]
  sub_haulers: User[]
  third_party_brokers: User[]
  third_party_contractors: User[]
  sub_hauler_preusers: PreUser[]
  third_party_broker_preusers: PreUser[]
  third_party_contractor_preusers: PreUser[]
  third_party_sub_haulers: User[]
  third_party_sub_hauler_preusers: PreUser[]
  // Form data uses user IDs for new contact fields
  sub_hauler_ids?: number[]
  third_party_broker_ids?: number[]
  third_party_contractor_ids?: number[]
  third_party_sub_hauler_ids?: number[]
  invitations: ProjectInvitation[]
  pendingInvites?: string[]
  // Performance optimization: Store complete user objects from form selection
  broker_users?: User[] // Added for performance optimization
  contractor_users?: User[] // Added for performance optimization
  sub_hauler_users?: User[] // Added for performance optimization
  // New multi-party invitation acceptance fields
  invitation_status?: InvitationStatus
  total_owner_invites_sent?: number
  total_owner_invites_accepted?: number
  // Payment schedule fields
  payment_type?: PaymentType
  payment_schedule?: PaymentScheduleEntry[]
  total_scheduled_payment?: string | number
  total_scheduled_fees?: string | number
  // Template recipient type for categorizing the project
  template_recipient_type?: string
  // Draft expiry for auto-delete after 45 days
  draft_expires_at?: string
  // Site supervisor form fields (for template-based project terms)
  site_supervisor_first_name?: string
  site_supervisor_last_name?: string
  site_supervisor_phone?: string
  site_supervisor_email?: string
  // Receiver-editable fields
  number_of_trucks_accepted?: number
  broker_fee?: string | number
  // Decline fields
  decline_reason?: string
  declined_by?: User
  declined_at?: string
  // Acceptance fields
  accepted_by?: User
  accepted_at?: string
  // Note attachments
  attachments?: Attachment[]
  // Sent timestamp
  sent_at?: string
  // Formatted date fields (with timezone)
  created_date_formatted?: string
  sent_at_formatted?: string
  // Snapshot fields (immutable)
  created_by_first_name?: string
  created_by_last_name?: string
  created_by_email?: string
  created_by_company_name?: string
  created_by_company_address?: string
  declined_by_first_name?: string
  declined_by_last_name?: string
  declined_by_email?: string
  declined_by_company_name?: string
  accepted_by_first_name?: string
  accepted_by_last_name?: string
  accepted_by_email?: string
  accepted_by_company_name?: string
  // Parent project fields (for sub-hauler downstream projects)
  parent_project_id?: number
  parent_project_label?: string
  // Sender stakeholder type (CONTRACTOR, BROKER, or SUB_HAULER)
  sender_stakeholder_type?: SenderStakeholderType
  // Original sender snapshot (resolved by traversing parent chain)
  original_sender_snapshot?: OriginalSenderSnapshot
  // All parties from parent project (for sub-contract reference display)
  parent_project_parties?: ParentProjectParties
}

export interface CreateProjectData
  extends Omit<
    Project,
    | 'id'
    | 'created_date'
    | 'updated_date'
    | 'status'
    | 'created_by'
    | 'invitations'
    | 'pendingInvites'
    | 'label'
    | 'brokers'
    | 'contractors'
    | 'site_supervisors'
    | 'site_supervisor_users'
    | 'project_managers'
    | 'administrators'
    | 'broker_preusers'
    | 'contractor_preusers'
    | 'assigned_brokers'
    | 'assigned_contractors'
    | 'sub_haulers'
    | 'third_party_brokers'
    | 'third_party_contractors'
    | 'third_party_sub_haulers'
    | 'sub_hauler_preusers'
    | 'third_party_broker_preusers'
    | 'third_party_contractor_preusers'
    | 'third_party_sub_hauler_preusers'
  > {
  broker_contacts: number[]
  contractor_contacts: number[]
  // Relationship fields accept User objects or IDs - service transforms to IDs
  site_supervisors: (User | number)[]
  contractors?: (User | number)[]
  brokers?: (User | number)[]
  sub_haulers?: (User | number)[]
  third_party_brokers?: (User | number)[]
  third_party_contractors?: (User | number)[]
  third_party_sub_haulers?: (User | number)[]
  project_managers?: (User | number)[]
  administrators?: (User | number)[]
  // Other fields
  trucks_number: string
  truck_types: string[]
  volume: string
  notes?: string
  template_recipient_type?: string
  status?: ProjectStatus // Optional - allows creating drafts
  // Optional pre-generated label from confirmation step preview
  label?: string
  // Parent project for sub-hauler downstream projects
  parent_project_id?: number
}

export interface UpdateProjectData extends Partial<CreateProjectData> {}

export interface ProjectInvite {
  id: string
  project: Project
  email: string
  invited_by: User
  accepted: boolean
  created_date: string
  updated_date: string
  // New multi-party invitation acceptance fields
  can_accept?: boolean
  invited_user_type?: string
}

export interface CreateProjectInviteData {
  email: string
}
